import React, { FunctionComponent } from 'react';
import { BarChart, Block, Heading, PendingContent, Spinner } from 'plume-ui';
import { ChartPeriodContext } from '../../../contexts/ChartPeriodContext';
import FormattedMessage from '../../../../../utils/components/FormattedMessage';
import { TimeLinePeriodTypes } from '../../../types';
import MonthDropdown from '../../MonthDropdown/MonthDropdown';
import TimeLinePicker from '../../TimelinePicker/TimelinePicker';
import { useTranslation } from 'react-i18next';
import { useTimeLine } from '../../../hooks/useTimeLine';
import { BarChartDataItem } from '../../../BarChart.fixture';
import { BlockStyles } from 'plume-ui/dist/components/Block/Block';
import { DependencyContainer } from '../../../../../DependencyContainer';
import { useRecoilValue } from 'recoil';
import { selectedCompanySelector } from '../../../../../state';
import { useFetchData } from '../../../../../hooks/useFetchData';

const addStylesToBlock = (current: BlockStyles) => ({
  ...current,
  root: `${current.root} RadiusServer`,
});
const { statusService } = new DependencyContainer();
const RadiusServerChartContainer: FunctionComponent = () => {
  const { t } = useTranslation();
  const {
    getContextDefaultValues,
    selectedPeriodType,
    selectedMonth,
    getPeriod,
    getType,
  } = useTimeLine();
  const radiusServerStatusGroups = [
    {
      label: t('radiusServerChart.primary'),
      color: '#333CFF',
      stroke: '#333CFF',
      key: 'groupC',
    },
    {
      label: t('radiusServerChart.secondary'),
      color: '#00BEDB',
      stroke: '#00BEDB',
      key: 'groupB',
    },
    {
      label: t('radiusServerChart.bothDown'),
      color: '#FC5FA3',
      stroke: '#FC5FA3',
      key: 'groupA',
    },
  ];
  const selectedCompany = useRecoilValue(selectedCompanySelector);
  const [loading, data, error] = useFetchData<BarChartDataItem[], any>(
    async () => {
      if (!selectedCompany) {
        return;
      }
      const [period, type] = [getPeriod(), getType()];
      return await statusService.getRadius(selectedCompany.id, period, type);
    },
    undefined,
    [selectedCompany, selectedPeriodType, selectedMonth],
    () => selectedCompany === undefined,
  );

  return (
    <ChartPeriodContext.Provider value={getContextDefaultValues()}>
      <PendingContent
        loading={loading}
        isError={Boolean(error)}
        hideContent
        loader={Spinner}
      >
        <Block classes={addStylesToBlock} id="RadiusServerChart">
          <Heading level={3} size="xl" medium>
            <FormattedMessage id="radiusServerChart.title" />
          </Heading>
          <div className="RadiusServer__timeline--container">
            <span className="RadiusServer__timeline--month">
              {selectedPeriodType === TimeLinePeriodTypes.CalendarMonth && (
                <MonthDropdown />
              )}
            </span>
            <TimeLinePicker />
          </div>
          {!data?.length ? (
            <div className="EmployeeOnboarding__empty--container">
              <FormattedMessage id="noDataAvailable" />
            </div>
          ) : (
            <BarChart
              stacked
              data={data}
              groups={radiusServerStatusGroups}
              config={{
                showPercentageOnTooltip: true,
                yAxisFormat:
                  selectedPeriodType === TimeLinePeriodTypes.Last24Hours
                    ? '60min'
                    : '24hr',
              }}
              legendPosition="bottom"
            />
          )}
        </Block>
      </PendingContent>
    </ChartPeriodContext.Provider>
  );
};
export default RadiusServerChartContainer;
